import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import { pl } from 'date-fns/locale';

import { cn } from '@/lib/cn';
import { buttonVariants } from './button';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

/**
 * shadcn-style Calendar wrapper around react-day-picker v9. Defaults to
 * Polish locale + Monday-first weeks (the Polish convention).
 */
export function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  weekStartsOn = 1,
  locale = pl,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      weekStartsOn={weekStartsOn}
      locale={locale}
      className={cn('p-3', className)}
      classNames={{
        months: 'flex flex-col sm:flex-row gap-4',
        month: 'flex flex-col gap-3',
        month_caption: 'flex justify-center pt-1 relative items-center w-full',
        caption_label: 'text-sm font-medium',
        nav: 'flex items-center gap-1',
        button_previous: cn(
          buttonVariants({ variant: 'ghost', size: 'icon' }),
          'absolute left-1 top-1 size-7 p-0 opacity-70 hover:opacity-100',
        ),
        button_next: cn(
          buttonVariants({ variant: 'ghost', size: 'icon' }),
          'absolute right-1 top-1 size-7 p-0 opacity-70 hover:opacity-100',
        ),
        month_grid: 'w-full border-collapse',
        weekdays: 'flex',
        weekday: 'text-text-muted rounded-md w-9 font-normal text-[0.8rem]',
        week: 'flex w-full mt-2',
        day: cn(
          'relative p-0 text-center text-sm focus-within:relative focus-within:z-20',
          '[&:has([aria-selected])]:bg-accent/10 [&:has([aria-selected].day-range-end)]:rounded-r-md',
          '[&:has([aria-selected].day-outside)]:bg-accent/5',
        ),
        day_button: cn(
          buttonVariants({ variant: 'ghost' }),
          'size-9 p-0 font-normal aria-selected:opacity-100',
        ),
        range_start: 'day-range-start aria-selected:bg-accent aria-selected:text-text-inverted',
        range_end: 'day-range-end aria-selected:bg-accent aria-selected:text-text-inverted',
        selected:
          'bg-accent text-text-inverted hover:bg-accent hover:text-text-inverted focus:bg-accent focus:text-text-inverted',
        today: 'bg-surface text-accent font-semibold',
        outside: 'day-outside text-text-subtle aria-selected:text-text-muted',
        disabled: 'text-text-disabled opacity-50',
        range_middle: 'aria-selected:bg-accent/10 aria-selected:text-text',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation, className: cls, ...rest }) =>
          orientation === 'left' ? (
            <ChevronLeft className={cn('size-4', cls)} {...rest} />
          ) : (
            <ChevronRight className={cn('size-4', cls)} {...rest} />
          ),
      }}
      {...props}
    />
  );
}
